import { Badge, Button } from '@parcel/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { AlertCircle, ArrowRight, Loader2, LogOut, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuth } from '../../lib/auth.js';
import { storePendingRedirect } from '../../lib/pending-redirect.js';
import {
  type WorkspaceSummary,
  getWorkspaceRedirectLocation,
  listWorkspaces,
} from '../../lib/workspaces.js';

function formatWorkspaceMeta(workspace: WorkspaceSummary): string {
  return workspace.companyDomain ?? workspace.slug;
}

function formatRole(role: WorkspaceSummary['role']): string {
  return role.charAt(0).toUpperCase() + role.slice(1);
}

export function WorkspacePicker() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const redirect =
    typeof window === 'undefined'
      ? null
      : new URLSearchParams(window.location.search).get('redirect');

  useEffect(() => {
    let active = true;

    async function loadWorkspaces() {
      setLoading(true);
      setError(null);

      try {
        const nextWorkspaces = await listWorkspaces();
        if (active) setWorkspaces(nextWorkspaces);
      } catch (err) {
        if (active) {
          setError(err instanceof Error ? err.message : 'Unable to load workspaces.');
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    void loadWorkspaces();

    return () => {
      active = false;
    };
  }, []);

  const chooseWorkspace = (workspace: WorkspaceSummary) => {
    if (workspace.onboardingComplete) {
      const location = getWorkspaceRedirectLocation(workspace.slug, redirect);
      if (location.search) {
        window.location.assign(location.href);
        return;
      }

      void navigate({ to: location.pathname });
      return;
    }

    // Welcome cannot carry the redirect in its URL; the tab keeps it for its Enter app button.
    if (redirect) storePendingRedirect(redirect);
    void navigate({
      to: '/$workspaceSlug/welcome',
      params: { workspaceSlug: workspace.slug },
    });
  };

  return (
    <main className="flex min-h-svh items-center justify-center bg-muted p-6 md:p-10">
      <div className="w-full max-w-xl space-y-6">
        <div className="space-y-3 text-center">
          <Link to="/" className="inline-flex">
            <img
              src="/brand/parcel-logo-black.svg"
              alt="Parcel"
              width="84"
              height="24"
              decoding="async"
              className="dark:hidden"
            />
            <img
              src="/brand/parcel-logo-white.svg"
              alt="Parcel"
              width="84"
              height="24"
              decoding="async"
              className="hidden dark:block"
            />
          </Link>
          <div className="space-y-1">
            <h1 className="font-semibold text-2xl tracking-normal">Choose a workspace</h1>
            <p className="text-muted-foreground text-sm">
              Select the Parcel workspace you want to open.
            </p>
          </div>
        </div>

        <section className="overflow-hidden rounded-lg border bg-background shadow-sm">
          {loading ? (
            <div className="flex min-h-48 items-center justify-center gap-2 text-muted-foreground text-sm">
              <Loader2 className="size-4 animate-spin" />
              Loading workspaces
            </div>
          ) : error ? (
            <div className="flex min-h-48 flex-col items-center justify-center gap-3 p-6 text-center">
              <AlertCircle className="size-5 text-destructive" />
              <div className="space-y-1">
                <p className="font-medium text-sm">Could not load workspaces</p>
                <p className="text-muted-foreground text-sm">{error}</p>
              </div>
            </div>
          ) : workspaces.length === 0 ? (
            <div className="flex min-h-48 flex-col items-center justify-center gap-3 p-6 text-center">
              <p className="font-medium text-sm">No workspaces yet</p>
              <p className="text-muted-foreground text-sm">
                Create a workspace to start using Parcel with your team.
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {workspaces.map((workspace) => (
                <button
                  key={workspace.id}
                  type="button"
                  onClick={() => chooseWorkspace(workspace)}
                  className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                >
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-brand/10 font-semibold text-brand text-sm">
                    {workspace.name.charAt(0).toUpperCase()}
                  </span>
                  <span className="min-w-0 flex-1 space-y-1">
                    <span className="block truncate font-medium text-sm">{workspace.name}</span>
                    <span className="block truncate text-muted-foreground text-sm">
                      {formatWorkspaceMeta(workspace)}
                    </span>
                  </span>
                  <Badge variant="secondary" className="shrink-0">
                    {formatRole(workspace.role)}
                  </Badge>
                  <ArrowRight className="size-4 shrink-0 text-muted-foreground" />
                </button>
              ))}
            </div>
          )}
        </section>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Button asChild variant="outline">
            <Link to="/signup/workspace">
              <Plus className="size-4" />
              Create a new workspace
            </Link>
          </Button>
          <Button type="button" variant="ghost" onClick={() => void signOut()}>
            <LogOut className="size-4" />
            Sign out
          </Button>
        </div>
      </div>
    </main>
  );
}
